import { DateRange, DateRangeID } from '@fullstory/client-api';
import {
  ComputeFunnelMetricsRequest,
  ComputeFunnelMetricsResponse,
  ComputeMetricResponse,
  FunnelMetricsServicePublic,
  GenerateOpportunitiesRequest,
  GenerateSignalsRequest,
  MetricsServicePublic,
  RequestAnalyticsIdType,
  SearchServicePublic,
  Signal,
} from '@fullstory/proto/fs/services/alexa';
import {
  FunnelsPublic,
  RevenueEvent,
  RevenueEventResponse,
} from '@fullstory/proto/fs/services/webber/conversions';
import {
  GetMutedOpportunityConfigsRequest,
  GetMutedOpportunityConfigsResponse,
  ImpactsPublic,
  MuteOpportunityRequest,
  MuteOpportunityResponse,
  MutedOpportunityConfig,
  UnmuteOpportunityRequest,
} from '@fullstory/proto/fs/services/webber/internal/impacts';
import { createGRPCAction, RequestActionType } from '@fullstory/redux-util';
import { defaultCacheKey } from '@fullstory/requests';
import { ActionType, createAction } from '@fullstory/redux-util/utils/types';
import { hashObject } from '@fullstory/util/hash';
import { UID, WithID } from '@fullstory/util/uid';
import jstz from 'jstz';
import { LegacyMetricsRequestBody } from '../../metrics/types';
import { metricsRequestBodyToLegacyComputeMetricRequest } from '../../metrics/utils';
import { SearchCacheKey } from '../../searchMeta';
import {
  GenerateOpportunitiesProgress,
  GenerateSignalsResponseWithID,
  ImpactTypes,
  Inputs,
  OpportunityResponseWithId,
} from '../types';
import { fullstorySelectors, searchMetaSelectors } from '../../../selectors';
import { NumericAggregationMethod } from '@fullstory/proto/fs/api/metrics';
import { getRequestAnalytics } from '@fullstory/util/alexa-request-analytics';
import { cloneDeep, uniqBy } from 'lodash';
import { AppThunkAction, AppDispatch, AppState } from '../../../types';
import {
  getCurrentSavedConfig,
  makeRevOppsMetricsRequestBody,
  getSignalParams,
  getFunnelMetricsRequest,
  getIsRevenueFunnel,
  getMutedOpportunityConfigsBySignalKey,
  getCannedDateRange,
  getGeneratedSignals,
  getCurrentSegment,
  getCurrentFunnel,
  getOpportunityParams,
  getGenerateOpportunitiesProgress,
  getOpportunityBatchVersion,
} from '../selectors';
import { Funnel } from '@fullstory/proto/fs/services/search-settings';

const { getSearchCacheKey } = searchMetaSelectors;
const { getOrgId } = fullstorySelectors;

export const ImpactActions = {
  // change the current segment
  changePendingSegment: (segmentId: string) =>
    createAction({
      type: ImpactTypes.ChangePendingSegment,
      segmentId,
    }),
  // change the current conversion
  changePendingFunnel: (funnelId: string, unnamed?: boolean) =>
    createAction({
      type: ImpactTypes.ChangePendingFunnel,
      funnelId,
      unnamed,
    }),
  // action fired on input modal completion
  changePendingInputs: (inputs: Inputs) =>
    createAction({
      type: ImpactTypes.ChangePendingInputs,
      inputs,
    }),
  // set the current detail view
  setOpportunityDetail: (id: string) =>
    createAction({
      type: ImpactTypes.SetOpportunityDetail,
      id,
    }),
  // Filtering Actions
  filterRageClicks: (filtered: boolean) =>
    createAction({
      type: ImpactTypes.FilterRageClicks,
      filtered,
    }),
  filterErrorClicks: (filtered: boolean) =>
    createAction({
      type: ImpactTypes.FilterErrorClicks,
      filtered,
    }),
  filterDeadClicks: (filtered: boolean) =>
    createAction({
      type: ImpactTypes.FilterDeadClicks,
      filtered,
    }),
  filterPagePerformance: (filtered: boolean) =>
    createAction({
      type: ImpactTypes.FilterPagePerformance,
      filtered,
    }),
  filterAjaxErrors: (filtered: boolean) =>
    createAction({
      type: ImpactTypes.FilterAjaxErrors,
      filtered,
    }),
  // custom date range options
  setPendingCannedDateRangeId: (id: DateRangeID) =>
    createAction({
      type: ImpactTypes.SetPendingCannedDateRangeID,
      id,
    }),
  setPendingStartDate: (date: Date) =>
    createAction({
      type: ImpactTypes.SetPendingStartDate,
      date,
    }),
  setPendingEndDate: (date: Date) =>
    createAction({
      type: ImpactTypes.SetPendingEndDate,
      date,
    }),
  // detail pagination actions
  setDetailStep: (page: number) =>
    createAction({
      type: ImpactTypes.SetDetailFocusedStep,
      page,
    }),
  // matching user sentence actions
  setUserSentenceConverted: (converted: boolean) =>
    createAction({
      type: ImpactTypes.SetUserSentenceConverted,
      converted,
    }),
  setUserSentenceHadExperience: (hadExperience: boolean) =>
    createAction({
      type: ImpactTypes.SetUserSentenceHadExperience,
      hadExperience,
    }),
  generateOpportunitiesBatchStarted: () =>
    createAction({
      type: ImpactTypes.GenerateOpportunitiesBatchStarted,
    }),
  generateOpportunitiesBatchSuccess: () =>
    createAction({
      type: ImpactTypes.GenerateOpportunitiesBatchSuccess,
    }),
  generateOpportunitiesBatchFailure: () =>
    createAction({
      type: ImpactTypes.GenerateOpportunitiesBatchFailure,
    }),
  setOpportunityBatchVersion: (opportunityBatchVersion: string) =>
    createAction({
      type: ImpactTypes.SetOpportunityBatchVersion,
      opportunityBatchVersion,
    }),
  resetOpportunityBatchVersion: () =>
    createAction({
      type: ImpactTypes.SetOpportunityBatchVersion,
      opportunityBatchVersion: '',
    }),
  generateOpportunityResults: (data: OpportunityResponseWithId) =>
    createAction({
      type: ImpactTypes.GenerateOpportunityResults,
      data,
    }),
  updateGenerateOpportunitiesProgress: (progress: GenerateOpportunitiesProgress) =>
    createAction({
      type: ImpactTypes.UpdateGenerateOpportunitiesProgress,
      progress,
    }),
};

export const ImpactRequests = {
  generateSignals: (
    req: Omit<GenerateSignalsRequest, 'timezone' | 'now'>,
    searchCacheKey: SearchCacheKey,
  ) => {
    const body: GenerateSignalsRequest = {
      ...req,
      now: null, // current time is implied
      timezone: jstz.determine().name(),
    };
    return createGRPCAction<
      typeof SearchServicePublic.GenerateSignals,
      GenerateSignalsResponseWithID
    >({
      modify: (resp): GenerateSignalsResponseWithID => ({
        signals: resp.signals.map(signal => ({
          ...signal,
          id: UID(),
        })),
      }),
      cacheKey: (url: string): string => {
        const { cacheOptions, ...requestCacheData } = body;
        return `${url}:${hashObject(requestCacheData)}:${hashObject(searchCacheKey)}`;
      },
    })(
      {
        type: ImpactTypes.GenerateSignalsRequest,
        grpcFunction: SearchServicePublic.GenerateSignals,
        readWrite: 'write',
        settings: {
          headers: { 'GRPC-Timeout': '5M' }, // allow for 5 minutes before context deadline exceeded
        },
        body,
      },
      ImpactTypes.GenerateSignalsSuccess,
      ImpactTypes.GenerateSignalsFailure,
    );
  },
  getRevenueEvent: (orgId: string) =>
    createGRPCAction<typeof FunnelsPublic.GetRevenueEvent, RevenueEvent | null>({
      modify: (resp: RevenueEventResponse): RevenueEvent | null => resp.revenueEvent,
    })(
      {
        type: ImpactTypes.RevenueEventRequest,
        grpcFunction: FunnelsPublic.GetRevenueEvent,
        readWrite: 'read',
        body: { orgId },
      },
      ImpactTypes.RevenueEventSuccess,
      ImpactTypes.RevenueEventFailure,
    ),
  computeFunnelMetrics: (
    req: Omit<ComputeFunnelMetricsRequest, 'timezone' | 'now'>,
    searchCacheKey: SearchCacheKey,
  ) => {
    const body: ComputeFunnelMetricsRequest = {
      ...req,
      now: null, // current time is implied
      timezone: jstz.determine().name(),
    };
    return createGRPCAction<typeof FunnelMetricsServicePublic.ComputeFunnelMetrics>({
      cacheKey: (url: string): string => {
        const { cacheOptions, ...requestCacheData } = body;
        return `${url}:${hashObject(requestCacheData)}:${hashObject(searchCacheKey)}`;
      },
    })(
      {
        type: ImpactTypes.ComputeFunnelMetricsRequest,
        grpcFunction: FunnelMetricsServicePublic.ComputeFunnelMetrics,
        settings: {
          headers: { 'GRPC-Timeout': '2M' }, // allow for 2 minutes before context deadline exceeded
          excluded: true,
        },
        readWrite: 'write',
        body,
      },
      ImpactTypes.ComputeFunnelMetricsSuccess,
      ImpactTypes.ComputeFunnelMetricsFailure,
    );
  },
  generateOpportunitiesReq: (req: GenerateOpportunitiesRequest, searchCacheKey: SearchCacheKey) =>
    createGRPCAction<typeof SearchServicePublic.GenerateOpportunities, OpportunityResponseWithId>({
      modify: (resp): OpportunityResponseWithId => ({
        opportunities: uniqBy(
          resp.opportunities.map(opportunity => {
            const { computedAt, ...rest } = opportunity;
            return {
              ...opportunity,
              id: hashObject(rest).toString(),
            };
          }),
          'id',
        ),
      }),
      // TODO(chrisjordan): is this worth extracting into a util method?
      cacheKey: (url: string): string => {
        const { cacheOptions, ...requestCacheData } = req;
        return `${url}:${hashObject(requestCacheData)}:${hashObject(searchCacheKey)}`;
      },
    })(
      {
        type: ImpactTypes.GenerateOpportunitiesRequest,
        grpcFunction: SearchServicePublic.GenerateOpportunities,
        body: req,
        settings: {
          headers: { 'GRPC-Timeout': '10M' }, // allow for 10 minutes before context deadline exceeded
        },
        readWrite: 'write',
      },
      ImpactTypes.GenerateOpportunitiesSuccess,
      ImpactTypes.GenerateOpportunitiesFailure,
    ),
  computeAOV: (body: LegacyMetricsRequestBody, searchCacheKey: SearchCacheKey) => {
    const reqBody = metricsRequestBodyToLegacyComputeMetricRequest(body);
    return createGRPCAction<typeof MetricsServicePublic.ComputeMetric, number>({
      modify: (resp: ComputeMetricResponse): number => {
        if (
          resp &&
          resp.data &&
          resp.data.table &&
          resp.data.table.length == 1 &&
          resp.data.table[0]
        ) {
          return resp.data.table[0].value;
        } else {
          throw new Error('Could not compute AOV. Metric API response was invalid.');
        }
      },
      cacheKey: url => `${url}:${hashObject(reqBody)}:${hashObject(searchCacheKey)}`,
    })(
      {
        type: ImpactTypes.FetchAverageOrderValueRequest,
        grpcFunction: MetricsServicePublic.ComputeMetric,
        body: reqBody,
        readWrite: 'write',
        settings: {
          headers: { 'GRPC-Timeout': '1M' }, // allow for 1 minute before context deadline exceeded
        },
      },
      ImpactTypes.FetchAverageOrderValueSuccess,
      ImpactTypes.FetchAverageOrderValueFailure,
    );
  },
  createRevenueEvent: (orgId: string, revenueEvent: RevenueEvent) =>
    createGRPCAction<typeof FunnelsPublic.CreateRevenueEvent, RevenueEvent | null>({
      modify: (resp: RevenueEventResponse): RevenueEvent | null => resp.revenueEvent,
    })(
      {
        type: ImpactTypes.RevenueEventRequest,
        grpcFunction: FunnelsPublic.CreateRevenueEvent,
        body: { orgId, revenueEvent },
        readWrite: 'write',
        invalidateCacheKey: defaultCacheKey(
          FunnelsPublic.GetRevenueEvent(() => new Promise<RevenueEventResponse>(_ => {}), {
            orgId: '',
          }).endpoint,
          { orgId },
        ),
        force: true,
      },
      ImpactTypes.RevenueEventSuccess,
      ImpactTypes.RevenueEventFailure,
    ),
  updateRevenueEvent: (orgId: string, revenueEvent: RevenueEvent) =>
    createGRPCAction<typeof FunnelsPublic.UpdateRevenueEvent, RevenueEvent | null>({
      modify: (resp: RevenueEventResponse): RevenueEvent | null => resp.revenueEvent,
    })(
      {
        type: ImpactTypes.RevenueEventRequest,
        grpcFunction: FunnelsPublic.UpdateRevenueEvent,
        body: { orgId, revenueEvent },
        readWrite: 'write',
        invalidateCacheKey: defaultCacheKey(
          FunnelsPublic.GetRevenueEvent(() => new Promise<RevenueEventResponse>(_ => {}), {
            orgId: '',
          }).endpoint,
          { orgId },
        ),
        force: true,
      },
      ImpactTypes.RevenueEventSuccess,
      ImpactTypes.RevenueEventFailure,
    ),
  computeTotalRevenue: (body: LegacyMetricsRequestBody, searchCacheKey: SearchCacheKey) => {
    const reqBody = metricsRequestBodyToLegacyComputeMetricRequest(body);
    return createGRPCAction<typeof MetricsServicePublic.ComputeMetric, number>({
      modify: (resp: ComputeMetricResponse): number => {
        if (
          resp &&
          resp.data &&
          resp.data.table &&
          resp.data.table.length == 1 &&
          resp.data.table[0]
        ) {
          return resp.data.table[0].value;
        } else {
          throw new Error('Could not compute Total Revenue. Metric API response was invalid.');
        }
      },
      cacheKey: url => `${url}:${hashObject(reqBody)}:${hashObject(searchCacheKey)}`,
    })(
      {
        type: ImpactTypes.FetchTotalRevenueRequest,
        grpcFunction: MetricsServicePublic.ComputeMetric,
        body: reqBody,
        readWrite: 'write',
        settings: {
          headers: { 'GRPC-Timeout': '1M' }, // allow for 1 minute before context deadline exceeded
        },
      },
      ImpactTypes.FetchTotalRevenueSuccess,
      ImpactTypes.FetchTotalRevenueFailure,
    );
  },
  getMutedOpportunitiesReq: (orgId: string, analysisId: string) => {
    const body: GetMutedOpportunityConfigsRequest = {
      orgId,
      analysisId,
    };
    return createGRPCAction<
      typeof ImpactsPublic.GetMutedOpportunityConfigs,
      MutedOpportunityConfig[]
    >({
      modify: (resp: GetMutedOpportunityConfigsResponse): MutedOpportunityConfig[] => {
        if (resp) {
          return resp.mutedOpportunityConfigs;
        } else {
          throw new Error('GetMutedOpportunities API response was invalid.');
        }
      },
      cacheKey: (url: string) => `${url}:${hashObject(Date.now())}`,
    })(
      {
        type: ImpactTypes.GetMutedOpportunityConfigsRequest,
        grpcFunction: ImpactsPublic.GetMutedOpportunityConfigs,
        body,
        readWrite: 'write',
      },
      ImpactTypes.GetMutedOpportunityConfigsSuccess,
      ImpactTypes.GetMutedOpportunityConfigsFailure,
    );
  },
  muteOpportunityReq: (orgId: string, analysisId: string, signal: Signal) => {
    const body: MuteOpportunityRequest = {
      orgId,
      analysisId,
      signal,
    };
    return createGRPCAction<typeof ImpactsPublic.MuteOpportunity, MutedOpportunityConfig | null>({
      modify: (resp: MuteOpportunityResponse): MutedOpportunityConfig | null => {
        if (resp) {
          return resp.mutedOpportunityConfig;
        } else {
          throw new Error('MuteOpportunity API response was invalid.');
        }
      },
    })(
      {
        type: ImpactTypes.MuteOpportunityRequest,
        grpcFunction: ImpactsPublic.MuteOpportunity,
        readWrite: 'write',
        body,
      },
      ImpactTypes.MuteOpportunitySuccess,
      ImpactTypes.MuteOpportunityFailure,
    );
  },
  unmuteOpportunityReq: (orgId: string, analysisId: string, signal: Signal) => {
    const body: UnmuteOpportunityRequest = {
      orgId,
      analysisId,
      signal,
    };
    return createGRPCAction<typeof ImpactsPublic.UnmuteOpportunity>()(
      {
        type: ImpactTypes.UnmuteOpportunityRequest,
        grpcFunction: ImpactsPublic.UnmuteOpportunity,
        readWrite: 'write',
        body,
      },
      ImpactTypes.UnmuteOpportunitySuccess,
      ImpactTypes.UnmuteOpportunityFailure,
    );
  },
};

const MAX_OPP_BATCH_SIZE = 20;
const MIN_OPP_BATCH_SIZE = 5;
export type ImpactThunkAction<R> = AppThunkAction<R, ImpactActions>;

export interface ImpactAnalyticsContext {
  signalParams: Omit<GenerateSignalsRequest, 'timezone' | 'now'>;
  funnelRequest: Omit<ComputeFunnelMetricsRequest, 'timezone' | 'now'>;
  mutedSignalsCount: number;
  requestId: string;
  dateRangeId: DateRangeID;
  isRevenueFunnel: boolean;
  funnel: Funnel | null;
}

export interface OpportunitiesAnalyticsContext {
  req: GenerateOpportunitiesRequest;
  segmentId: string;
  funnelId: string;
  cannedDateRange: DateRange;
  signalParams: Omit<GenerateSignalsRequest, 'timezone' | 'now' | 'cacheOptions'>;
  requestId: string;
}
export interface ImpactAnalytics {
  conversionsAnalysisRun: (params: ImpactAnalyticsContext) => void;
  opportunitiesStarted: (params: OpportunitiesAnalyticsContext) => void;
  opportunitiesFailed: (params: OpportunitiesAnalyticsContext & { status: number }) => void;
  opportunitiesSucceeded: (
    params: OpportunitiesAnalyticsContext & { resp: OpportunityResponseWithId },
  ) => void;
}

// TODO (jb): Rework this so the series of calls the app makes has more sense to it for today's use case. E.g. we can compute funnel metrics and signals separately. And we also probably don't need this concept of wrapping the calls from ImpactRequests (above).
export const ImpactAsyncActions = {
  computeAOV:
    (): ImpactThunkAction<Promise<void>> =>
    async (dispatch: AppDispatch, getState: () => AppState) => {
      const state = getState();
      const currentSavedConfig = getCurrentSavedConfig(state);
      const requestAnalytics = getRequestAnalytics(
        RequestAnalyticsIdType.CONVERSION,
        !!currentSavedConfig,
        currentSavedConfig ? currentSavedConfig.id.toString() : '',
      );
      const aovMetricRequestBody = makeRevOppsMetricsRequestBody(
        NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_AVERAGE,
        requestAnalytics,
      )(state);
      const searchCacheKey = getSearchCacheKey(state);
      if (aovMetricRequestBody) {
        dispatch(ImpactRequests.computeAOV(aovMetricRequestBody, searchCacheKey));
      }
    },
  computeTotalRevenue:
    (): ImpactThunkAction<Promise<void>> =>
    async (dispatch: AppDispatch, getState: () => AppState) => {
      const state = getState();
      const currentSavedConfig = getCurrentSavedConfig(state);
      const requestAnalytics = getRequestAnalytics(
        RequestAnalyticsIdType.CONVERSION,
        !!currentSavedConfig,
        currentSavedConfig ? currentSavedConfig.id.toString() : '',
      );

      const totalRevMetricRequestBody = makeRevOppsMetricsRequestBody(
        NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_SUM,
        requestAnalytics,
      )(state);
      const searchCacheKey = getSearchCacheKey(state);
      if (totalRevMetricRequestBody) {
        dispatch(ImpactRequests.computeTotalRevenue(totalRevMetricRequestBody, searchCacheKey));
      }
    },
  generateSignalsAndFunnels:
    (
      analytics: ImpactAnalytics,
    ): ImpactThunkAction<Promise<[ComputeFunnelMetricsResponse | null, void]>> =>
    async (dispatch: AppDispatch, getState: () => AppState) => {
      const state = getState();
      const orgId = getOrgId(state)!;
      const signalParams = getSignalParams(state);
      const funnelRequest = getFunnelMetricsRequest(state);
      const funnel = getCurrentFunnel(state);
      const isRevenueFunnel = getIsRevenueFunnel(state);
      const searchCacheKey = getSearchCacheKey(state);
      const mutedOppConfigs = getMutedOpportunityConfigsBySignalKey(state);
      const mutedSignalsCount = Object.keys(mutedOppConfigs).length;
      const lastBatch = state.impacts.opportunityBatchVersion;
      const lastFunnelMetrics = state.impacts.funnelMetrics.data;

      if (orgId && signalParams && funnelRequest) {
        const newBatchVersion = hashObject({
          orgId,
          signalParams,
          funnelRequest,
        }).toString();

        if (lastBatch === newBatchVersion) return [lastFunnelMetrics, undefined];

        dispatch(ImpactActions.setOpportunityBatchVersion(newBatchVersion));
        const signalParamsCopy = cloneDeep(signalParams);
        const cannedDateRange = getCannedDateRange(state)!;

        analytics.conversionsAnalysisRun({
          signalParams,
          funnelRequest,
          mutedSignalsCount,
          requestId: newBatchVersion,
          dateRangeId: cannedDateRange.id,
          isRevenueFunnel,
          funnel,
        });

        return Promise.all([
          dispatch(
            ImpactRequests.computeFunnelMetrics(
              funnelRequest as ComputeFunnelMetricsRequest,
              searchCacheKey,
            ),
          ), // This could be split off...
          dispatch(ImpactRequests.generateSignals(signalParamsCopy, searchCacheKey)).then(resp => {
            dispatch(
              ImpactActions.updateGenerateOpportunitiesProgress({
                completed: 0,
                total: resp.signals.length,
              }),
            );
            dispatch(ImpactAsyncActions.generateOpportunities(newBatchVersion, analytics));
          }),
        ]);
      } else {
        return [null, undefined];
      }
    },
  generateOpportunities:
    (batchId: string, analytics: ImpactAnalytics): ImpactThunkAction<Promise<void>> =>
    async (dispatch: AppDispatch, getState: () => AppState) => {
      const state = getState();
      const signals = getGeneratedSignals(state);
      const currentSegment = getCurrentSegment(state);
      const segmentId = currentSegment?.segment?.segment?.id ?? '';
      const currentFunnel = getCurrentFunnel(state)!;
      const cannedDateRange = getCannedDateRange(state)!;
      const signalParams = getSignalParams(state)!;
      const opportunityParams = getOpportunityParams(state)!;
      const currentCompletedProgress = getGenerateOpportunitiesProgress(state);
      const searchCacheKey = getSearchCacheKey(state);

      const analyticsContext: OpportunitiesAnalyticsContext = {
        req: {
          ...opportunityParams,
          signals: signals.slice(),
          now: null, // current time is implied, analytics event will have timestamp
          timezone: jstz.determine().name(),
        },
        segmentId,
        funnelId: currentFunnel.id,
        cannedDateRange,
        signalParams,
        requestId: batchId,
      };

      analytics.opportunitiesStarted(analyticsContext);
      dispatch(ImpactActions.generateOpportunitiesBatchStarted());
      return dispatch(
        ImpactAsyncActions.generateOpportunitiesBatch(
          batchId,
          opportunityParams,
          signals,
          searchCacheKey,
          analytics,
          analyticsContext,
          currentCompletedProgress,
        ),
      );
    },
  generateOpportunitiesBatch:
    (
      batchId: string,
      opportunityParams: Omit<GenerateOpportunitiesRequest, 'signals' | 'now' | 'timezone'>,
      signals: WithID<Signal>[],
      searchCacheKey: SearchCacheKey,
      analytics: ImpactAnalytics,
      analyticsContext: OpportunitiesAnalyticsContext,
      currentCompletedProgress: GenerateOpportunitiesProgress,
      batchSize: number = MAX_OPP_BATCH_SIZE,
    ): ImpactThunkAction<Promise<void>> =>
    async (dispatch: AppDispatch, getState: () => AppState) => {
      const req: GenerateOpportunitiesRequest = {
        ...opportunityParams,
        signals: signals.slice(0, batchSize),
        now: null, // current time is implied
        timezone: jstz.determine().name(),
      };
      let resp: OpportunityResponseWithId;
      try {
        resp = await dispatch(ImpactRequests.generateOpportunitiesReq(req, searchCacheKey));
      } catch (error: any) {
        const state = getState();
        if (batchId != getOpportunityBatchVersion(state)) {
          return;
        }
        const status = error.code;
        analytics.opportunitiesFailed({
          ...analyticsContext,
          status,
        });

        if (batchSize > MIN_OPP_BATCH_SIZE) {
          return dispatch(
            ImpactAsyncActions.generateOpportunitiesBatch(
              batchId,
              opportunityParams,
              signals,
              searchCacheKey,
              analytics,
              analyticsContext,
              currentCompletedProgress,
              Math.max(MIN_OPP_BATCH_SIZE, batchSize / 2),
            ),
          );
        } else {
          dispatch(ImpactActions.generateOpportunitiesBatchFailure());
          throw error;
        }
      }
      const currentProgress = {
        ...currentCompletedProgress,
        completed: currentCompletedProgress.completed + req.signals.length,
      };
      analytics.opportunitiesSucceeded({
        ...analyticsContext,
        resp,
      });
      const state = getState();
      if (batchId != getOpportunityBatchVersion(state)) {
        return;
      }
      if (resp) {
        dispatch(ImpactActions.generateOpportunityResults(resp));
        dispatch(ImpactActions.updateGenerateOpportunitiesProgress(currentProgress));
      }
      if (signals.length <= batchSize) {
        dispatch(ImpactActions.generateOpportunitiesBatchSuccess());
        return;
      }
      return dispatch(
        ImpactAsyncActions.generateOpportunitiesBatch(
          batchId,
          opportunityParams,
          signals.slice(batchSize),
          searchCacheKey,
          analytics,
          analyticsContext,
          currentProgress,
          Math.min(MAX_OPP_BATCH_SIZE, batchSize * 2),
        ),
      );
    },
};

export type ImpactActions =
  | ActionType<typeof ImpactActions>
  | RequestActionType<typeof ImpactRequests>;
